import {TextInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import Parser from 'html-react-parser';
import React from 'react';
import type {NotificationRuleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {NotificationAttributeGrid} from 'src/app/elements/NotificationAttributeGrid';
import {FormRichTextEditorV2} from 'src/app/forms/FormRichTextEditor/FormRichTextEditorV2';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {useGetNotificationRuleVariablesQuery} from 'src/redux/queries/notificationRule';
import {substituteNotificationVariablesWithSampleValue} from '../notificationRuleHelpers';

export interface EditNotificationRuleContentProps {
  data: NotificationRuleResponse;
  onDataChange(data: NotificationRuleResponse): void;
  disabled: boolean;
  subjectError?: string;
  bodyError?: string;
  onSubjectChange?: () => void;
  onBodyChange?: () => void;
}
export const EditNotificationRuleContent: React.FC<
  EditNotificationRuleContentProps
> = ({
  data,
  onDataChange,
  disabled,
  subjectError,
  bodyError,
  onSubjectChange,
  onBodyChange,
}) => {
  const {
    data: variablesResp,
    isLoading,
    isFetching,
  } = useGetNotificationRuleVariablesQuery(
    {
      ruleType: data.ruleType,
      module: data.metadata.module ?? ModuleEnum.Invalid,
      courseChangeType: data.metadata.courseChangeType,
      formDefinitionId: data.metadata.formDefinitionId,
    },
    {}
  );
  const variables = variablesResp?.variables ?? [];

  function handleSubjectChange(value: string): void {
    onSubjectChange?.();
    onDataChange({
      ...data,
      subject: value,
    });
  }

  function handleBodyChange(_name: string, value: string): void {
    onBodyChange?.();
    onDataChange({
      ...data,
      body: value,
    });
  }

  return (
    <Stack gap="md">
      <div>
        <Title order={4}>Content</Title>
        <Text size="sm" c="dimmed">
          Configure the subject and body of the notification email. Use the
          available variables below to insert dynamic values.
        </Text>
      </div>

      {isLoading || isFetching ? (
        <Loader />
      ) : (
        <>
          <TextInput
            label="Notification Subject"
            description={
              'Use {{VARIABLE_NAME}} syntax to insert dynamic values.'
            }
            value={data.subject ?? ''}
            onChange={e => handleSubjectChange(e.currentTarget.value)}
            disabled={disabled}
            error={subjectError}
            withAsterisk
          />
          <FormViewRow
            label={'Subject Preview (sample values)'}
            value={Parser(
              substituteNotificationVariablesWithSampleValue(
                data.subject ?? '',
                variables
              )
            )}
          />
          <Box mt="lg">
            <FormRichTextEditorV2
              field={{
                name: 'body',
                label: 'Notification Body',
                type: 'richtext',
                required: false,
                disabled: disabled,
              }}
              value={data.body ?? ''}
              error={bodyError}
              onChange={handleBodyChange}
              disableExternalContent={true}
              readOnly={disabled}
            />
          </Box>
          <Box mih="200px">
            <FormViewRow
              label={'Body Preview (sample values)'}
              value={Parser(
                substituteNotificationVariablesWithSampleValue(
                  data.body ?? '',
                  variables
                )
              )}
            />
          </Box>

          <NotificationAttributeGrid
            variables={variables}
            allowCopyToClipboard={!disabled}
          />
        </>
      )}
    </Stack>
  );
};
